import * as React from "react";
import { AutoComplete } from "@/core/components/ui/autocomplete";
import { Stack } from "@/core/components/ui/stack";
import {
  TypeableDatePicker,
  TypeableDatePickerProps,
} from "@/core/components/ui/typeable-date-picker";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { generateTimeOptions } from "@/core/utils/date";

interface DateTimePickerProps extends TypeableDatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

export const DateTimePicker = ({
  value,
  onChange,
  label = "Date",
  placeholder = "mm/dd/yyyy",
  required,
  error,
  ...props
}: DateTimePickerProps) => {
  const [startTime, setStartTime] = React.useState<string>("");
  const [endTime, setEndTime] = React.useState<string>("");
  const [startTimeSearch, setStartTimeSearch] = React.useState("");
  const [endTimeSearch, setEndTimeSearch] = React.useState("");
  const startTimeRef = React.useRef<HTMLInputElement>(null);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [hasInteractedWithTime, setHasInteractedWithTime] =
    React.useState(false);

  // Update internal state when prop changes
  React.useEffect(() => {
    if (value) {
      setSelectedDate(value);
      // Only set times from value if user has not interacted with time fields
      if (!hasInteractedWithTime) {
        setStartTime(dayjs(value).format("h:mm a"));
        const endTimeDate = new Date(value);
        endTimeDate.setMinutes(endTimeDate.getMinutes() + 30);
        setEndTime(dayjs(endTimeDate).format("h:mm a"));
        setStartTimeSearch(dayjs(value).format("h:mm a"));
        setEndTimeSearch(dayjs(endTimeDate).format("h:mm a"));
      }
    } else {
      setSelectedDate(null);
      setStartTime("");
      setEndTime("");
      setStartTimeSearch("");
      setEndTimeSearch("");
      setHasInteractedWithTime(false);
    }
  }, [value, hasInteractedWithTime]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);

    // Only update the full value if we have both date and time
    if (date && startTime) {
      const updatedDate = new Date(date);
      try {
        // Parse the start time
        const [time, period] = startTime.split(" ");
        const [hours, minutes] = time.split(":");
        const isPM = period.toLowerCase() === "pm";
        let startHour = parseInt(hours);
        // Handle 12 AM/PM cases
        if (startHour === 12) {
          startHour = isPM ? 12 : 0;
        } else {
          startHour = isPM ? startHour + 12 : startHour;
        }

        updatedDate.setHours(startHour, parseInt(minutes));
        onChange?.(updatedDate);
      } catch (error) {
        console.error("Error parsing time:", error);
      }
    } else if (date === null) {
      // Handle case when date is cleared
      onChange?.(null);
    }

    // Focus the start time input after a short delay if it hasn't been set
    if (date && !startTime) {
      setTimeout(() => {
        startTimeRef.current?.focus();
      }, 100);
    }
  };

  const handleStartTimeChange = (value: string) => {
    setHasInteractedWithTime(true);
    setStartTime(value);
    setStartTimeSearch(value);

    try {
      // Parse the start time
      const [time, period] = value.split(" ");
      const [hours, minutes] = time.split(":");
      const isPM = period.toLowerCase() === "pm";
      let startHour = parseInt(hours);
      // Handle 12 AM/PM cases
      if (startHour === 12) {
        startHour = isPM ? 12 : 0;
      } else {
        startHour = isPM ? startHour + 12 : startHour;
      }

      // Parse the current end time if it exists
      let endHour = 0;
      let endMinute = 0;
      if (endTime) {
        const [endTimeStr, endPeriod] = endTime.split(" ");
        const [endHours, endMinutes] = endTimeStr.split(":");
        const isEndPM = endPeriod.toLowerCase() === "pm";
        const parsedEndHour = parseInt(endHours);
        // Handle 12 AM/PM cases for end time
        if (parsedEndHour === 12) {
          endHour = isEndPM ? 12 : 0;
        } else {
          endHour = isEndPM ? parsedEndHour + 12 : parsedEndHour;
        }
        endMinute = parseInt(endMinutes);
      }

      // Calculate total minutes for comparison
      const startTotalMinutes = startHour * 60 + parseInt(minutes);
      const endTotalMinutes = endHour * 60 + endMinute;

      // If end time is before start time or empty, set it to start time + 30 minutes
      if (!endTime || startTotalMinutes >= endTotalMinutes) {
        const newEndDate = dayjs()
          .hour(startHour)
          .minute(parseInt(minutes) + 30);
        const newEndTime = newEndDate.format("h:mm a");
        setEndTime(newEndTime);
        setEndTimeSearch(newEndTime);
      }

      // Update the date value if we have a valid date
      if (selectedDate) {
        const dateObj = new Date(selectedDate);
        dateObj.setHours(startHour, parseInt(minutes));
        onChange?.(dateObj);
      }
    } catch (error) {
      console.error("Error parsing time:", error);
    }
  };

  const handleEndTimeChange = (value: string) => {
    setHasInteractedWithTime(true);
    setEndTime(value);
    setEndTimeSearch(value);
  };

  const startTimeOptions = React.useMemo(() => generateTimeOptions(), []);

  const endTimeOptions = React.useMemo(() => {
    if (!startTime) {
      return generateTimeOptions();
    }
    return generateTimeOptions(startTime);
  }, [startTime]);

  return (
    <Stack fullWidth>
      <TypeableDatePicker
        value={selectedDate}
        onChange={handleDateChange}
        label={label}
        placeholder={placeholder}
        required={required}
        {...props}
      />

      <Stack direction="row" fullWidth>
        <AutoComplete
          ref={startTimeRef}
          selectedValue={startTime}
          onSelectedValueChange={handleStartTimeChange}
          searchValue={startTimeSearch}
          onSearchValueChange={setStartTimeSearch}
          items={startTimeOptions}
          placeholder="Start Time"
          label="Start Time"
          required={required}
          fullWidth
        />
        <AutoComplete
          selectedValue={endTime}
          onSelectedValueChange={handleEndTimeChange}
          searchValue={endTimeSearch}
          onSearchValueChange={setEndTimeSearch}
          items={endTimeOptions}
          placeholder="End Time"
          label="End Time"
          required={required}
          fullWidth
        />
      </Stack>
      {error && (
        <Typography variant="captionW1" textColor="negative">
          {error}
        </Typography>
      )}
    </Stack>
  );
};
